import React from 'react';
import {
  Autocomplete as MuiAutocomplete,
  TextField,
  Chip,
  Box,
  CircularProgress,
  styled,
  Paper,
} from '@mui/material';
import type { AutocompleteProps as MuiAutocompleteProps } from '@mui/material';
import { Search, Clear, ExpandMore } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Option interface
export interface AutoCompleteOption {
  id: string | number;
  label: string;
  value: string | number;
  disabled?: boolean;
  icon?: React.ReactNode;
  description?: string;
}

// Custom autocomplete variants
export type AutoCompleteVariant = 'outlined' | 'filled' | 'standard';
export type AutoCompleteSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomAutoCompleteProps
  extends Omit<
    MuiAutocompleteProps<AutoCompleteOption, boolean, boolean, boolean>,
    'options' | 'renderInput' | 'size'
  > {
  variant?: AutoCompleteVariant;
  size?: AutoCompleteSize;
  options: AutoCompleteOption[];
  label?: string;
  placeholder?: string;
  description?: string;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  creatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
}

// Styled components
const StyledTextField = styled(TextField)<{ size?: AutoCompleteSize; error?: boolean }>(({
  theme,
  size,
  error,
}) => {
  const sizeConfig = {
    small: {
      '& .MuiInputBase-root': {
        minHeight: 36,
        fontSize: '0.75rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.75rem',
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        minHeight: 44,
        fontSize: '0.875rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    },
    large: {
      '& .MuiInputBase-root': {
        minHeight: 52,
        fontSize: '1rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '1rem',
      },
    },
  };

  return {
    ...sizeConfig[size || 'medium'],
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1),
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: error ? theme.palette.error.main : theme.palette.grey[300],
        borderWidth: 1.5,
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.common.white,
        boxShadow: `0 0 0 2px ${alpha(
          error ? theme.palette.error.main : theme.palette.primary.main,
          0.2
        )}`,
        '& fieldset': {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          borderWidth: 2,
        },
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[100],
      },
    },
  };
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  '& .MuiAutocomplete-option': {
    minHeight: 44,
    padding: theme.spacing(1, 1.5),
    '&[aria-selected="true"]': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

const OptionContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
});

const OptionText = styled(Box)({
  flex: 1,
});

const OptionDescription = styled('div')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

export const AutoComplete: React.FC<CustomAutoCompleteProps> = ({
  variant = 'outlined',
  size = 'medium',
  options,
  label,
  placeholder,
  description,
  error = false,
  helperText,
  loading = false,
  searchable = true,
  clearable = true,
  creatable = false,
  onCreateOption, // Used conditionally for creatable functionality
  multiple = false,
  value,
  onChange,
  ...props
}) => {
  const displayHelperText = error ? helperText : description || helperText;

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AutoCompleteOption) => (
    <li {...props} key={option.id}>
      <OptionContent>
        {option.icon && option.icon}
        <OptionText>
          <div>{option.label}</div>
          {option.description && <OptionDescription>{option.description}</OptionDescription>}
        </OptionText>
      </OptionContent>
    </li>
  );

  const renderTags = (value: AutoCompleteOption[], getTagProps: (props: { index: number }) => Record<string, unknown>) =>
    value.map((option, index) => (
      <Chip
        key={option.id}
        label={option.label}
        size={size === 'small' ? 'small' : 'medium'}
        variant="outlined"
        {...getTagProps({ index })}
        sx={{
          margin: 0.25,
          '& .MuiChip-deleteIcon': {
            fontSize: '1rem',
          },
        }}
      />
    ));

  return (
    <MuiAutocomplete
      {...props}
      options={options}
      value={value}
      multiple={multiple}
      loading={loading}
      clearOnBlur={!creatable}
      selectOnFocus={creatable}
      handleHomeEndKeys={creatable}
      freeSolo={creatable}
      onChange={(event, newValue, reason) => {
        // Handle creatable functionality
        if (creatable && reason === 'createOption' && typeof newValue === 'string' && onCreateOption) {
          onCreateOption(newValue);
          return;
        }
        onChange?.(event, newValue, reason);
      }}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;
        return option.label;
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={renderOption}
      renderTags={renderTags}
      PaperComponent={StyledPaper}
      popupIcon={<ExpandMore />}
      clearIcon={clearable ? <Clear /> : null}
      renderInput={params => (
        <StyledTextField
          {...params}
          variant={variant}
          size={undefined}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={displayHelperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {searchable && (
                  <Search
                    sx={{
                      color: 'text.secondary',
                      mr: 1,
                      fontSize: size === 'small' ? '1rem' : '1.25rem',
                    }}
                  />
                )}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && (
                  <CircularProgress
                    color="inherit"
                    size={size === 'small' ? 16 : 20}
                    sx={{ mr: 1 }}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          paddingRight: '39px !important',
        },
        ...props.sx,
      }}
    />
  );
};

export default AutoComplete;
